import { uniqueIdFromTreeId } from "library/unique-id-from-tree-id.mjs"
function wrapParagraph(item) {
    if (!item) return ""
    if (item.startsWith("<p>")) return item
    return `<p>${item}</p>`
}
const validCountUnits = ["Nr", "System"]
const doNotTailor = Symbol("doNotTailor")
// A content override is "set" only when it carries real content — empty string / null means
// "fall back to master text", so an empty override never blanks a title or body. Exported because
// the client tailoring surface uses the same test to decide whether a stored override must keep
// displaying (read-only) when the rollout flag is off.
function hasOverride(value) {
    return value != null && value !== ""
}
const extendSchedule = (schedule, payload, doNotRemoveTasks, key) => {
    if (!payload || !schedule) return
    if (payload.tailoringName) {
        schedule.title = `${schedule.title} (${payload.tailoringName})`
        // console.trace("Extended to", schedule.title) // Uncomment to see extensions to schedule
    }
    const shouldTailorSchedule = !schedule[doNotTailor]
    if (payload.uniqueCode && !schedule.$code && shouldTailorSchedule) {
        schedule.code = `${schedule.code}-${`${payload.uniqueCode}`.padLeft(4, "0")}`
        schedule.$code = true
    }
    if (schedule.intro) {
        // An explicit override replaces the master intro body; otherwise keep the legacy prepend of
        // the tailoring `introduction`. Notes are always additive.
        schedule.intro.content = hasOverride(payload.introContentOverride)
            ? payload.introContentOverride
            : `${wrapParagraph(payload.introduction)}${wrapParagraph(schedule.intro.content)}`
        schedule.intro.note = hasOverride(payload.introNoteOverride)
            ? payload.introNoteOverride
            : `${wrapParagraph(schedule.intro.note)}${wrapParagraph(payload.notes)}`
    }
    if (!schedule.tasks) {
        schedule.tasks = []
    }
    schedule.assets = getAssets(schedule, payload, key)
    schedule.systemAssets = payload.assets
    schedule.units = payload.units
    if (shouldTailorSchedule) {
        schedule.tasks = schedule.tasks
            .map((t) => ({
                ...t,
            }))
            .map((task) => {
                if (!task) return null
                const payloadTask = payload?.tasks?.[task.id]
                // Adjust the minutes for this task based on the number of assets, or the measurement etc
                const originalMinutes = task.minutes
                task.count = validCountUnits.includes(task.unitOfMeasure) ? payload.units?.[task.unitOfMeasure] || 1 : 1
                task._minutes = +(payloadTask?.minutes ?? task.minutes ?? 0)
                task.minutes = +(payloadTask?.minutes ?? task.minutes ?? 0) * task.count
                if (!payloadTask) {
                    return task
                }
                if (payloadTask.included === false && !doNotRemoveTasks) {
                    return null
                }
                task.included = payloadTask.included
                task.classification = payloadTask.classification ?? task.classification
                task.unitOfMeasure = payloadTask.unitOfMeasure ?? task.unitOfMeasure

                // See if we should show the number of minutes for the task (it's been tailored)
                if ((payloadTask?.minutes ?? originalMinutes ?? 0) !== originalMinutes) {
                    task.$showMinutes = true
                }
                task.frequency = payloadTask.frequency ?? task.frequency
                // An explicit override replaces the master task content/note; otherwise keep the
                // legacy prepend of the tailoring introduction/notes.
                task.content = hasOverride(payloadTask.contentOverride)
                    ? payloadTask.contentOverride
                    : `${wrapParagraph(payloadTask.introduction)}${wrapParagraph(task.content)}`
                task.note = hasOverride(payloadTask.noteOverride)
                    ? payloadTask.noteOverride
                    : `${wrapParagraph(task.note)}${wrapParagraph(payloadTask.notes)}`
                // An explicit title override replaces the master task title; empty falls back to master.
                task.title = hasOverride(payloadTask.titleOverride) ? payloadTask.titleOverride : task.title
                task.skillSet = payloadTask.skillSet ?? task.skillSet
                return task
            })
            .filter(Boolean)
    }
}
function payloadKey(tree, group, key = "default") {
    key = uniqueIdFromTreeId(key)
    if (key.startsWith(`GT*`) && (!group || group === tree)) {
        return `global!${key}`
    }
    return `${uniqueIdFromTreeId(group)}!${key}`
}
function getAssets(schedule, payload, key = "nokey") {
    return schedule.tasks
        .map("unitOfMeasure")
        .unique()
        .filter((f) => validCountUnits.includes(f))
        .map((unit) => {
            const number = +(payload.units?.[unit] || 1)
            const output = []
            for (let i = 0; i < number; i++) {
                const asset = payload.units?.[`${unit}_${i + 1}`]
                const validAsset = asset && typeof asset === "string" ? asset : undefined
                if (validAsset) {
                    output.push(validAsset)
                } else {
                    output.push(`${key ?? "Unspecified Asset"}.${i}`)
                }
            }
            return output
        })
        .flat(Infinity)
}
export { extendSchedule }
export { payloadKey }
export { doNotTailor }
export { hasOverride }
